/* eslint no-param-reassign: 0 */

import getUuid from 'widget/helpers/uuid';
import { MESSAGE_STATUS, MESSAGE_TYPE } from 'shared/constants/messages';

export default () => {
  if (!Array.prototype.last) {
    Object.assign(Array.prototype, {
      last() {
        return this[this.length - 1];
      },
    });
  }
};

export const isEmptyObject = obj =>
  Object.keys(obj).length === 0 && obj.constructor === Object;

export const isJSONValid = value => {
  try {
    JSON.parse(value);
  } catch (e) {
    return false;
  }
  return true;
};

export const getTypingUsersText = (users = []) => {
  const count = users.length;
  const [firstUser, secondUser] = users;

  if (count === 1) {
    return ['TYPING.ONE', { user: firstUser.name }];
  }

  if (count === 2) {
    return [
      'TYPING.TWO',
      { user: firstUser.name, secondUser: secondUser.name },
    ];
  }

  return ['TYPING.MULTIPLE', { user: firstUser.name, count: count - 1 }];
};

// Aceleres 2026-09-09: os object URLs criados para a bolha otimista sobreviviam
// a sessao inteira — `createObjectURL` sem `revokeObjectURL` prende os bytes do
// arquivo na memoria do separador. Agora eles tambem viajam para a mensagem do
// servidor (ver `mergeLocalMediaPreviews`), entao nao da para revogar assim que
// o POST volta: sao eles que evitam o balao cinzento. Guardamos os ultimos e
// libertamos os antigos, que ja nao estao no ecra de ninguem.
const MAX_LOCAL_PREVIEWS = 20;
const localPreviewUrls = [];

const trackLocalPreviewUrl = url => {
  localPreviewUrls.push(url);
  while (localPreviewUrls.length > MAX_LOCAL_PREVIEWS) {
    const antigo = localPreviewUrls.shift();
    try {
      URL.revokeObjectURL(antigo);
    } catch {
      // Um object URL ja revogado (ou um ambiente sem a API) nao e erro.
    }
  }
};

/**
 * Aceleres 2026-09-09: quando a resposta do servidor substitui a mensagem
 * otimista, a chave do `v-for` muda (uuid -> id numerico) e a bolha e
 * REMONTADA. Sem isto o `data_url` local (blob, os bytes que o navegador ja
 * tem) e deitado fora e a bolha volta a zero, a descarregar do servidor a mesma
 * imagem que acabou de enviar: medido em homologacao, 5,3 s de balao cinzento
 * para uma foto de 4,9 MB. O endereco real continua a ser o `data_url` — e ele
 * que a galeria, o download e o encaminhamento precisam; o blob entra num campo
 * proprio, so para desenhar enquanto o remoto nao chega.
 */
export const mergeLocalMediaPreviews = (pendingMessage, serverMessage) => {
  const locais = (pendingMessage?.attachments || []).map(attachment =>
    typeof attachment?.data_url === 'string' &&
    attachment.data_url.startsWith('blob:')
      ? attachment.data_url
      : null
  );

  if (
    !locais.some(Boolean) ||
    !Array.isArray(serverMessage?.attachments) ||
    !serverMessage.attachments.length
  ) {
    return serverMessage;
  }

  return {
    ...serverMessage,
    attachments: serverMessage.attachments.map((attachment, index) =>
      locais[index]
        ? { ...attachment, local_preview_url: locais[index] }
        : attachment
    ),
  };
};

export const createPendingMessage = data => {
  const timestamp = Math.floor(new Date().getTime() / 1000);
  const tempMessageId = getUuid();
  const { message, file, files, contactCard } = data;
  let pendingFiles = [];
  if (Array.isArray(files)) pendingFiles = files;
  if (!Array.isArray(files) && file) pendingFiles = [file];
  const tempAttachments = pendingFiles.map((pendingFile, index) => ({
    id: `${tempMessageId}-${index}`,
    file_type: (() => {
      const contentType = String(pendingFile?.type || '').toLowerCase();
      if (contentType.startsWith('image/')) return 'image';
      if (contentType.startsWith('video/')) return 'video';
      if (contentType.startsWith('audio/')) return 'audio';
      return 'file';
    })(),
    data_url: (() => {
      const podeCriar =
        typeof URL !== 'undefined' &&
        typeof URL.createObjectURL === 'function' &&
        typeof Blob !== 'undefined' &&
        pendingFile instanceof Blob;
      if (!podeCriar) return undefined;

      const objectUrl = URL.createObjectURL(pendingFile);
      trackLocalPreviewUrl(objectUrl);
      return objectUrl;
    })(),
    file_name: pendingFile?.name || '',
    file_size: pendingFile?.size || 0,
    content_type: pendingFile?.type || '',
  }));
  // Aceleres: o cartao de contato ja tem todo o conteudo na hora do clique —
  // nome, telefone e foto. Descrevendo o anexo otimista com os mesmos campos
  // que o servidor devolve, a bolha nasce como cartao e nao pisca de texto
  // vazio para cartao quando a resposta chega.
  const pendingContactAttachments = [
    {
      id: tempMessageId,
      file_type: 'contact',
      fallback_title: contactCard?.phone_number || '',
      meta: {
        name: contactCard?.name || '',
        thumbnail: contactCard?.thumbnail || '',
        contact_id: contactCard?.contact_id ?? null,
      },
    },
  ];
  let pendingAttachments = null;
  if (contactCard) {
    pendingAttachments = pendingContactAttachments;
  } else if (pendingFiles.length) {
    pendingAttachments = tempAttachments;
  }

  const pendingMessage = {
    ...data,
    content: message || null,
    id: tempMessageId,
    echo_id: tempMessageId,
    status: MESSAGE_STATUS.PROGRESS,
    created_at: timestamp,
    message_type: MESSAGE_TYPE.OUTGOING,
    conversation_id: data.conversationId,
    attachments: pendingAttachments,
  };

  return pendingMessage;
};

export const convertToAttributeSlug = text => {
  return text
    .toLowerCase()
    .replace(/[^\w ]+/g, '')
    .replace(/ +/g, '_');
};

export const convertToCategorySlug = text => {
  return text
    .toLowerCase()
    .replace(/[^\w ]+/g, '')
    .replace(/ +/g, '-');
};

export const convertToPortalSlug = text => {
  return text
    .toLowerCase()
    .replace(/[^\w ]+/g, '')
    .replace(/ +/g, '-');
};

/**
 * Strip curly braces, commas and leading/trailing whitespace from a search key.
 * Eg. "{{contact.name}}," => "contact.name"
 * @param {string} searchKey
 * @returns {string}
 */
export const sanitizeVariableSearchKey = (searchKey = '') => {
  return searchKey
    .replace(/[{}]/g, '') // remove all curly braces
    .replace(/,/g, '') // remove commas
    .trim();
};

/**
 * Convert underscore-separated string to title case.
 * Eg. "round_robin" => "Round Robin"
 * @param {string} str
 * @returns {string}
 */
export const formatToTitleCase = str => {
  return (
    str
      ?.replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase())
      .trim() || ''
  );
};
